/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { HtmlSanitizer } from '../../../helpers/sanitizer/html-sanitizer';
import { EmbedCode, MediaUrlData, MediumEditorData } from '../../../models';

@Component({
  selector: 'ptl-resource-html-preview',
  templateUrl: './html-preview.component.html',
  styleUrls: ['./html-preview.component.scss', './html-preview-new.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceHtmlPreviewComponent implements OnChanges {
  /** Receives the EditorContent object */
  @Input() content: Pick<MediaUrlData, 'position' | 'type'> | undefined;
  @Input() withTruncateLines = false;
  @ViewChild('htmlEl', { static: true }) public htmlEl?: ElementRef;
  hidePreview = false;
  isFocusable = false;
  isExpanded = false;

  get shouldShowSeeMore(): boolean {
    return this.withTruncateLines && this.content?.type === 'PARAGRAPH' && !this.isExpanded && this.hasContentOverflow();
  }

  get containerClass(): string {
    if (!this.withTruncateLines || this.isExpanded) {
      return '';
    }
    return 'line-clamp-3';
  }

  private hasContentOverflow(): boolean {
    if (!this.content) return false;

    let text = '';
    if (this.content.type === 'PARAGRAPH') {
      text = (this.content as MediumEditorData).content || '';
    }

    return text.length > 150;
  }

  ngOnChanges(changes: SimpleChanges) {
    this.htmlEl.nativeElement.innerHTML = '';
    this.content = changes.content.currentValue;
    this.hidePreview = false;
    if (this.content) {
      if (this.content.type === 'MEDIA_URL') {
        this.parseAndAppendHtml((this.content as MediaUrlData).contentHtml);
      } else if (this.content.type === 'PARAGRAPH') {
        this.parseAndAppendHtml((this.content as MediumEditorData).content);
      } else if (this.content.type === 'SEPARATOR') {
        this.parseAndAppendHtml((this.content as MediumEditorData).content);
      } else if (this.content.type === 'EMBED_CODE') {
        this.parseAndAppendHtml(HtmlSanitizer.sanitizeEmbedCode((this.content as EmbedCode).code));
      } else if (this.content.type === 'TABLE') {
        this.parseAndAppendHtml((this.content as MediumEditorData).content);
      } else {
        this.hidePreview = true;
      }
    } else {
      this.hidePreview = true;
    }
  }

  private parseAndAppendHtml(html: string) {
    if (html && !!html.length && this.htmlEl) {
      if (this.isOnlyEmptyParagraphs(html)) {
        this.hidePreview = true;
        return;
      }

      const normalizedHtml = this.normalizeEmptyParagraphs(html);
      // createContextualFragment enables the execution of <script> if present in our html
      const fragment = document.createRange().createContextualFragment(normalizedHtml);
      this.htmlEl.nativeElement.appendChild(fragment);
      this.isFocusable = true;
    } else {
      this.hidePreview = true;
    }
  }

  private isOnlyEmptyParagraphs(html: string): boolean {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const children = Array.from(doc.body.children);

      if (children.length === 0) {
        const bodyText = (doc.body.textContent ?? '').replace(/\u00a0/g, '').trim();
        return bodyText.length === 0;
      }

      const allParagraphs = children.every((child) => child.tagName === 'P');
      if (!allParagraphs) {
        return false;
      }

      return children.every((child) => {
        const p = child as HTMLParagraphElement;
        const hasElementChildren = p.children.length > 0;
        if (hasElementChildren) {
          return false;
        }

        const text = (p.textContent ?? '').replace(/\u00a0/g, '').trim();
        return text.length === 0;
      });
    } catch {
      return false;
    }
  }

  /**
   * Ensure empty paragraphs are visible by replacing truly empty <p> with &nbsp;.
   * We keep paragraphs that contain meaningful children (e.g., images, embeds).
   */
  private normalizeEmptyParagraphs(html: string): string {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const paragraphs = Array.from(doc.body.querySelectorAll('p'));

      paragraphs.forEach((p) => {
        // Only replace when the paragraph has no element children
        // and its text content is empty or only whitespace / &nbsp;
        const hasElementChildren = p.children.length > 0;
        if (hasElementChildren) {
          return;
        }

        const text = (p.textContent ?? '').replace(/\u00a0/g, '').trim();
        const isEmpty = text.length === 0;

        // Additionally handle cases where the HTML is literally "<p></p>" or "<p>&nbsp;</p>"
        const rawHtml = (p.innerHTML ?? '').replace(/\u00a0/g, '').trim();
        const isRawEmpty = rawHtml === '' || rawHtml === '&nbsp;' || rawHtml === '&#160;';

        if (isEmpty && isRawEmpty) {
          p.innerHTML = '&nbsp;';
        }
      });

      return doc.body.innerHTML;
    } catch {
      return html;
    }
  }

  toggleExpansion(event: Event) {
    event.stopPropagation();
    this.isExpanded = !this.isExpanded;
  }
}
